import { Link, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { Banknote } from 'lucide-react';
import { get } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/layout/PageHeader';
import { Skeleton } from '@/components/ui/skeleton';
import {
  dollars,
  periodLabel,
  payoutStatus,
  ratePct,
  type PayoutRow,
} from '@/lib/payouts';
import { storePath } from '@/lib/store';

type Payload = { payouts?: PayoutRow[] };

export function PayoutTable({
  payouts,
  showStore = true,
}: {
  payouts: PayoutRow[];
  showStore?: boolean;
}) {
  return (
    <div className="overflow-hidden rounded-[12px] border border-black/[0.05] bg-surface shadow-card">
      <div className="flex items-center gap-3 border-b border-divider px-3.5 py-2 text-[10px] font-semibold uppercase tracking-[0.05em] text-subtle">
        {showStore && <span className="min-w-0 flex-1">Store</span>}
        <span className={showStore ? 'w-20 shrink-0' : 'min-w-0 flex-1'}>Period</span>
        <span className="w-24 shrink-0 text-right">Team 10%</span>
        <span className="w-24 shrink-0 text-right">Rep 7%</span>
        <span className="w-24 shrink-0 text-right">Status</span>
      </div>
      {payouts.map((p) => {
        const status = payoutStatus(p);
        return (
          <div key={p.id} className="flex items-center gap-3 border-b border-divider px-3.5 py-2.5 last:border-b-0">
            {showStore && (
              <div className="min-w-0 flex-1">
                {p.storeId ? (
                  <Link
                    to={storePath(p.storeId)}
                    className="truncate text-[12.5px] font-medium text-primary underline-offset-2 hover:underline"
                  >
                    {p.storeName || p.storeId}
                  </Link>
                ) : (
                  <span className="truncate text-[12.5px]">{p.storeName || p.id}</span>
                )}
                <p className="truncate text-[11px] text-muted">
                  {dollars(p.revenue)} revenue
                  {p.salesRepName ? ` · ${p.salesRepName}` : ''}
                </p>
              </div>
            )}
            <div className={showStore ? 'w-20 shrink-0' : 'min-w-0 flex-1'}>
              <p className="text-[12.5px] font-medium">{periodLabel(p)}</p>
              {!showStore && (
                <p className="text-[11px] text-muted">{dollars(p.revenue)} revenue</p>
              )}
            </div>
            <div className="w-24 shrink-0 text-right">
              <p className="text-[12.5px]">{dollars(p.teamCommission)}</p>
              <p className="text-[10.5px] text-muted">{ratePct(p.commissionRate, 10)}</p>
            </div>
            <div className="w-24 shrink-0 text-right">
              <p className="text-[12.5px]">{dollars(p.salesRepCommission)}</p>
              <p className="text-[10.5px] text-muted">{ratePct(p.salesRepCommissionRate, 7)}</p>
            </div>
            <div className="w-24 shrink-0 text-right">
              <Badge className={status.className}>{status.label}</Badge>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function Payouts() {
  const [params] = useSearchParams();
  const storeFilter = params.get('store') || '';
  const { data, isLoading, error } = useSWR<Payload>('/api/payouts', get, { shouldRetryOnError: false });
  const all = data?.payouts || [];
  const payouts = storeFilter ? all.filter((p) => p.storeId === storeFilter) : all;

  return (
    <div className="px-7 py-6">
      <PageHeader
        title="Payouts"
        subtitle="Quarterly team 10% and sales-rep 7% statements. Stores stay open — this is not a close-out. Read-only; the job lives on teamstore-closeout."
      />

      {isLoading ? (
        <div className="space-y-2 rounded-[12px] border border-black/[0.05] bg-surface p-3 shadow-card">
          <Skeleton className="h-7 w-full" />
          <Skeleton className="h-7 w-2/3" />
        </div>
      ) : error ? (
        <div className="rounded-[14px] border border-dashed border-border p-8 text-center">
          <p className="text-[13px] font-medium text-danger">Couldn’t load payouts</p>
          <p className="mt-1 text-[12px] text-muted">Firestore did not return statements. This is not an empty quarter.</p>
        </div>
      ) : payouts.length === 0 ? (
        <div className="rounded-[14px] border border-dashed border-border p-8 text-center">
          <Banknote className="mx-auto mb-2 h-5 w-5 text-subtle" aria-hidden />
          <p className="text-[13px] font-medium">No quarterly payouts yet</p>
          <p className="mt-1 text-[12px] text-muted">
            {storeFilter
              ? 'No statements for this store. teamStorePayouts lights up after the quarterly payout job (PR 357) merges and runs.'
              : 'teamStorePayouts is empty until the quarterly payout job (PR 357) merges and runs. This page will list store, period, team 10%, rep 7%, and status.'}
          </p>
        </div>
      ) : (
        <>
          {storeFilter && (
            <p className="mb-3 text-[12px] text-muted">
              Showing {payouts.length} statement{payouts.length === 1 ? '' : 's'} for this store.{' '}
              <Link to="/payouts" className="text-primary underline-offset-2 hover:underline">All payouts</Link>
            </p>
          )}
          <PayoutTable payouts={payouts} />
        </>
      )}
    </div>
  );
}
